import { ReactNode, ReactElement, lazy, Suspense } from 'react';
import _ from 'lodash';
import * as WidgetsThingsStructs from "../WidgetsThingsStructs";
import { IWidgetDescription, WidgetDescriptionToPlegmaThing, IWidgetDataValidateResp } from '@UI/Widgets/WidgetStructs';
import { isBool, isDefined, IsNullOrEmpty } from '@Utils/Micro';
import { WebThingPortType } from '@API/WebNodeTypes';
import { WebThing, WebThingPort } from '@API/WebThing';
import { Plegma } from '@API/Api';
import { IWidgetThingsClass } from '../Utils/IWidgetThingsClass';
import {
    MapsData,
    MapLayer,
    MarkerSize,
    MarkerShape,
    MapsPortKeys,
    MapAccumulateClusteringAlgorithm,
    PanningMode,
    OutlineStyle,
    MarkerShapeDeprecated,
} from "@Primitive/Map/LeafletMapStructs";
import { LeafletMapJsonSchemaValidator, MapInitializationJsonSchemeValidator, selectFeaturesValidator, settingsPanelsValidator } from '@Primitive/Map/LeafletMapValidator';
import { LazyLoadingAnimation } from '@Primitive/Lazy';

const Maps = lazy(() => import('./MapsAsync'));
const MapsWidgetSettings = lazy(() => import('./MapsAsync').then(module => ({ default: module.MapsWidgetSettings })));
// =======================================================================================================
// Maps Description interface
// ------------------------------------------------------------------------------------------------
export class MapsWidget extends IWidgetThingsClass<MapsData> {
    public Render(key: string, width: number, height: number): ReactNode {
        return <WidgetsThingsStructs.LazyWidget >
            <Maps />
        </WidgetsThingsStructs.LazyWidget>;
    }
    // ------------------------------------------------------------------------------------------------
    public RenderWidgetSettings(key: string, width: number, height: number): ReactElement<any> {
        const parent = this.Parent;
        return <Suspense fallback={<LazyLoadingAnimation />}>
            <MapsWidgetSettings
                key={"Settings-" + key}
                width={width}
                height={height}
                WidgetData={_.cloneDeep(this.WidgetData)} // copy of the data and is return the copy in the update cb
                WidgetDefaultValues={_.cloneDeep(this.Description.DefaultValues)}
                onWidgetDataUpdate={(d) => this.WidgetDataUpdate(d)}
                ThingKey={this.ThingKey}
                NodeKey={this.NodeKey}
                SettingListToolbar={isDefined(parent) ? parent.SettingsListToolbar : null}
            />
        </Suspense>;
    }
    // ------------------------------------------------------------------------------------------------
    public initThing() {
        // Thing description
        let plegmaThing = WidgetDescriptionToPlegmaThing(this.Description);

        // Ports
        let ports: WebThingPort[] = [
            new WebThingPort({
                Name: "Init Map",
                PortKey: MapsPortKeys.Init,
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Input,
                State: ""
            }, WebThingPortType.Trigger, data => this.ValidateValues(MapsPortKeys.Init, data)),

            new WebThingPort({
                Name: "GeoJSON",
                PortKey: MapsPortKeys.GeoJSON,
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Input,
                State: ""
            }, WebThingPortType.Data, data => this.ValidateValues(MapsPortKeys.GeoJSON, data)),

            new WebThingPort({
                Name: "Insert or Update",
                PortKey: MapsPortKeys.Add,
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Input,
                State: ""
            }, WebThingPortType.Trigger, data => this.ValidateValues(MapsPortKeys.Add, data)),

            new WebThingPort({
                Name: 'Select Features',
                PortKey: MapsPortKeys.SelectFeatures,
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Input,
                State: ''
            }, WebThingPortType.Data, data => this.ValidateValues(MapsPortKeys.SelectFeatures, data)),

            new WebThingPort({
                Name: 'Settings Panels',
                PortKey: MapsPortKeys.SettingsPanels,
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Input,
                State: ''
            }, WebThingPortType.Data, data => this.ValidateValues(MapsPortKeys.SettingsPanels, data)),

            new WebThingPort({
                Name: "Reset",
                PortKey: MapsPortKeys.Reset,
                Type: Plegma.ePortType.String,
                ioDirection: Plegma.ioPortDirection.Input,
                ConfFlags: Plegma.ePortConf.IsTrigger,
                State: ""
            }, WebThingPortType.Trigger),

            new WebThingPort({
                Name: "Feature",
                PortKey: MapsPortKeys.Feature,
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Output,
                State: ""
            }, WebThingPortType.Trigger),

            new WebThingPort({
                Name: "Cluster",
                PortKey: MapsPortKeys.Cluster,
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Output,
                State: ""
            }, WebThingPortType.Trigger),

            new WebThingPort({
                Name: "On Popup Data Change",
                PortKey: MapsPortKeys.OnPopupDataChange,
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Output,
                State: ""
            }, WebThingPortType.Trigger),

            new WebThingPort({
                Name: 'On Click',
                PortKey: MapsPortKeys.OnClick,
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Output,
                State: ''
            }, WebThingPortType.Trigger),

            new WebThingPort({
                Name: 'Save',
                PortKey: MapsPortKeys.Save,
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Output,
                State: ''
            }, WebThingPortType.Trigger),

            new WebThingPort({
                Name: 'On Settings Panel Button Pressed',
                PortKey: MapsPortKeys.OnSettingsPanelButtonPressed,
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Output,
                State: ''
            }, WebThingPortType.Trigger),

            new WebThingPort({
                Name: 'On Settings Panel Element Changed',
                PortKey: MapsPortKeys.OnSettingsPanelElementChanged,
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Output,
                State: ''
            }, WebThingPortType.Trigger),

            new WebThingPort({
                Name: 'On History Event Change',
                PortKey: MapsPortKeys.OnHistoryEventChange,
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Output,
                State: ''
            }, WebThingPortType.Trigger),

            new WebThingPort({
                Name: 'On Right Panel Infotip Change',
                PortKey: MapsPortKeys.OnRightPanelInfotipChange,
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Output,
                State: ''
            }, WebThingPortType.Data),

            new WebThingPort({
                Name: 'On Right Panel Dynamic Form Element Change',
                PortKey: MapsPortKeys.OnRightPanelDynamicFormElementChange,
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Output,
                State: ''
            }, WebThingPortType.Data),

            new WebThingPort({
                Name: 'On Right Panel Dynamic Form Button Click',
                PortKey: MapsPortKeys.OnRightPanelDynamicFormButtonClick,
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Output,
                State: ''
            }, WebThingPortType.Data)
        ];

        // Create the thing
        let thing = new WebThing(this.WebModule, plegmaThing, ports);
        this.ThingKey = thing.ThingKey;
    }
    // ------------------------------------------------------------------------------------------------
    public static GetDefaultWidgetDataStatic(): MapsData {
        return {
            Title: "Maps",
            InitialZoom: 5,
            //InitialCenter: ,
            MinZoom: 0,
            MaxZoom: 21,
            SelectedLayer: MapLayer.OpenStreetMapMapnik,
            ShowClusters: false,
            ZoomToBoundsOnClusterClick: false,
            SingleMarkerMode: false,
            ShowCoverageOnHover: true,
            AutoFitBounds: false,
            PanningMode: PanningMode.Disabled,
            ShowMapScale: true,
            OpenFeaturePopupInitially: false,
            ClosePopupOnClick: false,
            PopupsMaxHeight: 600,
            DisableClusteringAtZoom: undefined,
            ClusterBackgroundColor: 'blue',
            ClusterFontColor: 'white',
            AccumulateClusteringAlgorithm: MapAccumulateClusteringAlgorithm.Count,
            markerSettings: {
                shape: MarkerShape.pin,
                size: MarkerSize.medium,
                dynamicSize: false,
                scaleFactor: 0.1,
                color: 'blue',
                icon: '',
                iconColor: 'white',
                heatmapView: false,
                customIcons: [],
                layer: null,
                zIndex: 10
            },
            UseHighlights: false,
            ShowMarkerValue: false,
            Highlights: {},
            PictureMode: false,
            PictureDefaultURL: "",
            EditModeSupport: false,
            BackgroundColor: "lightgray",
            BtnsVis: {
                ZoomInOut: true,
                FitBounds: true,
                TogglePopups: true,
                ToggleContent: true,
                Layers: true,
                Search: true
            },
            EnableLocateUser: false,
            Watch: false,
            SetView: true,
            LocationMaxZoom: 19,
            EnableHighAccuracy: false,
            ZoomDelta: 1,
            EditSettings: {
                AllowDrawingFeatures: null,
                AutoSave: false,
                FillColor: "blue",
                FillOpacity: 0.2,
                LineColor: "blue",
                LineOpacity: 0.8,
                RandomID: false,
                StrokeWeight: 4,
                AllowDeletion: true,
                AllowResizeMove: true,
                MaxNumOfNewItems: null
            },
            PolygonSettings: {
                fillColor: "blue",
                fillOpacity: 0.2,
                lineColor: "blue",
                lineOpacity: 0.8,
                strokeWeight: 4,
                zIndex: 0
            },
            PersistentPopup: false,
            UseStateStorage: false,
            selectionStyle: {
                outlineStyle: OutlineStyle.dashed,
                outlineColor: 'rgba(255, 0, 0, 0.6)',
                outlineWidth: 4,
                outlineOffset: 2
            },
            DetectRetina: false
        };
    }
    // ------------------------------------------------------------------------------------------------
    public GetDefaultWidgetData() { return MapsWidget.GetDefaultWidgetDataStatic(); }
    // ------------------------------------------------------------------------------------------------
    public static GetWidgetDesc(): IWidgetDescription {
        let data: MapsData = MapsWidget.GetDefaultWidgetDataStatic();

        // fill sample highlights
        data.Highlights['layer'] = [{
            from: 80,
            to: 100,
            color: 'rgba(200,50,50,.75)',
            textColor: 'rgba(255,255,255,1)'
        }];

        return {
            Title: "Maps",
            WidgetTitle: "",
            Key: "MapsWidget",
            Type: "MapsWidget",
            Data: data,
            MinH: 1,
            MinW: 1,
            ShowExtraMenu: false,
            IconURI: WidgetsThingsStructs.IconURIPath + "/svg/map.svg"
        };
    }
    // ------------------------------------------------------------------------------------------------
    public GetDefaultValues(): { [key: string]: any } {
        return {
            [MapsPortKeys.GeoJSON]: null,
            [MapsPortKeys.Add]: null,
            [MapsPortKeys.Reset]: false,
            [MapsPortKeys.SelectFeatures]: null,
            [MapsPortKeys.SettingsPanels]: null
        };
    }
    // ------------------------------------------------------------------------------------------------
    public ValidateValues(key: string, value: any): IWidgetDataValidateResp {
        if (key == MapsPortKeys.Add || key == MapsPortKeys.GeoJSON) {
            return LeafletMapJsonSchemaValidator().DataValidateString(value);
        }
        else if (key == MapsPortKeys.Init) {
            return MapInitializationJsonSchemeValidator().DataValidateString(value);
        }
        else if (key == MapsPortKeys.SelectFeatures) {
            return selectFeaturesValidator().DataValidateString(value);
        }
        else if (key == MapsPortKeys.SettingsPanels) {
            return settingsPanelsValidator().DataValidateString(value);
        }
        else if (key == MapsPortKeys.Reset) {
            if (!isBool(value))
                return { valid: false, errors: "Reset value must be boolean." }
        }
        return { valid: true, errors: "" };
    }
    // ------------------------------------------------------------------------------------------------
    public MigrateDefaultWidgetData(data: MapsData): MapsData {
        // migrate `markerSettings.shape`
        if (isDefined(data.markerSettings)) {
            // try cast shape to number
            const number = Number(data.markerSettings.shape);
            // if number; then we need to migrate
            if (!isNaN(number)) {
                // check value
                switch (number) {
                    case MarkerShapeDeprecated.circle:
                        data.markerSettings.shape = MarkerShape.circle;
                        break;
                    case MarkerShapeDeprecated.pin:
                        data.markerSettings.shape = MarkerShape.pin;
                        break;
                    case MarkerShapeDeprecated.pureIcon:
                        data.markerSettings.shape = MarkerShape.pureIcon;
                        break;
                    case MarkerShapeDeprecated.squarePin:
                        data.markerSettings.shape = MarkerShape.squarePin;
                        break;
                }
            }
        }

        ValidateMapSettings(data);
        return data;
    }
    // ------------------------------------------------------------------------------------------------
    public GetLocalizationDictionary() {
        if (!IsNullOrEmpty(this.Description.WidgetTitle))
            return {
                [WidgetsThingsStructs.WidgetLocalizationKeys.Title]: this.Description.WidgetTitle
            };
        else
            // nothing to expose
            return {};
    }
    // ------------------------------------------------------------------------------------------------
    GetDataPortKeyStatic(): string { return MapsPortKeys.GeoJSON; };
    // ------------------------------------------------------------------------------------------------
}

/**
 * Used to validate maps settings
 * @param data
 */
function ValidateMapSettings(data: MapsData) {
    // disable edit mode if clustering is enabled
    if (data.ShowClusters === true && data.EditModeSupport === true)
        data.EditModeSupport = false;

}
// =======================================================================================================